import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ConfirmStep from './steps/confirm.step';
import CreatedStep from './steps/created.step';
import GetLogo from '../../components/GetLogo';
import LookupStep from './steps/lookup.step';
import OrgStep from './steps/org.step';
import PasswordStep from './steps/password.step';
import { AccentButtonMediumOpenSans, ButtonMediumOpenSans } from '../../../../styledComponents/styles/Buttons.styled';
import { createUserAccount } from '../../../../actions/authenticationActions';
import ScreenReaderText from '../../../travelerProfile/components/base/ScreenReaderText.styled';

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 23px 0;
  padding: 0 10px;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const PreviousContainer = styled.div`
  flex: 1;
  text-align: left;
`;

const NextContainer = styled.div`
  flex: 1;
  text-align: right;
`;

export default function SignupForm() {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const [step, setStep] = useState(1);
  const [showScreenReader, setShowScreenReader] = useState(false);
  const [signUpError, setSignUpError] = useState({});
  /*
   * steps:
   *   1. Email lookup
   *   2. Enter Password
   *   3. Confirm/Select organization
   *   4. Confirm values
   *   5. Account created
   * */
  const schema = yup.object().shape({
    email: yup
      .string()
      .email('Invalid email address format')
      .required('Email must be entered'),
    password: yup
      .string()
      .required('Password must be entered')
      .min(8, 'Password must be at least 8 characters')
      .matches(/^(?=.*[A-Z])/, 'Password must contain an uppercase letter')
      .matches(/^(?=.*[!\"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~])/, 'Password must contain a special character'),
    passwordConfirm: yup
      .string()
      .required('Password must be entered')
      .oneOf([yup.ref('password')], 'Passwords do not match'),
    organizationId: yup.string().required('Organization must be selected'),
    organizationName: yup.string(),
  });

  const { control, handleSubmit, formState, getValues, setValue, setError, trigger, clearErrors } = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      passwordConfirm: '',
      organizationId: '',
      organizationName: '',
    },
    mode: 'onChange',
  });

  const onSubmitHandler = data => {
    const submitData = {
      client_account_id: data.organizationId,
      email: data.email,
      password: data.password,
      password_confirmation: data.passwordConfirm,
    };
    dispatch(createUserAccount(submitData));
  };

  async function validationCheckStep2() {
    setShowScreenReader(false);
    const validationCheck = await Promise.resolve(trigger(['email', 'password', 'passwordConfirm']));
    if (!validationCheck) {
      setTimeout(() => {
        setShowScreenReader(true);
      }, 1000);
    }

    return validationCheck;
  }

  async function validationCheckStep3() {
    setShowScreenReader(false);
    const validationCheck = await Promise.resolve(
      trigger(['email', 'password', 'passwordConfirm', 'organizationId', 'organizationName']),
    );

    if (!validationCheck) {
      setTimeout(() => {
        setShowScreenReader(true);
      }, 1000);
    }

    return validationCheck;
  }

  const handleSetStep = newStep => {

    setSignUpError({});
    setTimeout(()=>{
      setSignUpError(formState?.errors);
    },0);

    if (newStep === 1 || newStep === 2) {
      // If starting back at beginning, clear error messages
      clearErrors();
      setStep(newStep);
    } else if (newStep === 3) {
      //before going to step 3 validate step 2
      validationCheckStep2().then(isValid => {
        newStep = isValid ? newStep : 2;
        setStep(newStep);
      });
    } else if (newStep === 4) {
      //before going to step 4 validate step 3
      validationCheckStep3().then(isValid => {
        newStep = isValid ? newStep : 3;
        setStep(newStep);
      });
    } else {
      setStep(newStep);
    }
  };

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const email = urlParams.get('email') || '';
    setValue('email', email, {});
  }, []);

  useEffect(() => {
    if (auth && auth.organization && auth.organization.id) {
      setValue('organizationId', auth.organization.id);
      setValue('organizationName', auth.organization.org_name);
    }
  }, [auth]);

  useEffect(() => {
    setShowScreenReader(false);
    if (formState?.errors?.organizationId?.message) {
      setTimeout(() => {
        setShowScreenReader(true);
      }, 1000);
    }
  }, [formState?.errors?.organizationId?.message]);

  return (
    <>
      <GetLogo />
      <StepContainer>
        {showScreenReader && (
          <ScreenReaderText>
            {/* Rendering error messages in a list */}
            {Object.keys(formState?.errors).length > 0 && (
              <div role="alert" aria-live="assertive">
                <ul>
                  {Object.keys(formState?.errors).map((key, index) => (
                    <>{formState?.errors?.[key]?.message && <li>{formState?.errors?.[key]?.message}</li>}</>
                  ))}
                </ul>
              </div>
            )}
          </ScreenReaderText>
        )}

        <form id={'createAccount'} onSubmit={handleSubmit(onSubmitHandler)}>
          {step === 1 && (
            <LookupStep
              control={control}
              errors={formState.errors}
              setValue={setValue}
              getValues={getValues}
              isValid={formState.isValid}
              setError={setError}
              nextStep={step => {
                handleSetStep(step);
              }}
            />
          )}
          {step === 2 && (
            <PasswordStep
              control={control}
              //errors={formState.errors}
              errors={signUpError}
              setValue={setValue}
              getValues={getValues}
              isValid={formState.isValid}
            />
          )}
          {step === 3 && (
            <OrgStep
              control={control}
              errors={formState.errors}
              setValue={setValue}
              getValues={getValues}
              isValid={formState.isValid}
            />
          )}
          {step === 4 && (
            <ConfirmStep
              control={control}
              errors={formState.errors}
              setValue={setValue}
              getValues={getValues}
              isValid={formState.isValid}
              nextStep={step => {
                handleSetStep(step);
              }}
            />
          )}
          {step === 5 && typeof window === 'object' && (
            <CreatedStep
              control={control}
              errors={formState.errors}
              setValue={setValue}
              getValues={getValues}
              isValid={formState.isValid}
            />
          )}
          {step !== 1 && step !== 4 && step !== 5 && (
            <ButtonContainer>
              <PreviousContainer>
                <ButtonMediumOpenSans
                  aria-label="Previous step"
                  onClick={() => {
                    handleSetStep(step - 1);
                  }}
                  id="previousBtn"  
                >
                  Previous
                </ButtonMediumOpenSans>
              </PreviousContainer>
              <NextContainer>
                <AccentButtonMediumOpenSans
                  aria-label="Next step"
                  onClick={() => {
                    handleSetStep(step + 1);
                  }}
                  id="nextBtn"
                >
                  Next
                </AccentButtonMediumOpenSans>
              </NextContainer>
            </ButtonContainer>
          )}
        </form>
      </StepContainer>
    </>
  );
}
